import type { AgenticLogicRuleDto } from "@intelligems/ig-types";

import { useAgenticLogicAssignments } from "@src/api/agentic-logic/list-agentic-logic-assignments";
import { useAgenticLogicPlacements } from "@src/api/agentic-logic/list-agentic-logic-placements";
import { useAgenticLogicTemplates } from "@src/api/agentic-logic/list-agentic-logic-templates";

import { buildRuleListRows, type RuleListRow } from "../rules/ruleListRows";

/**
 * "Where can this run, and what is running it" for one rule, from the SAME
 * join the rules list uses — so the list and the detail page cannot disagree.
 * A secondary query that has not answered yet, or that failed, leaves that
 * half of the byline null ("unknown") rather than empty ("known: none"): the
 * shared-rule save consent gate reads `usedIn`, and an in-flight read that
 * looked like "used nowhere" would let a save skip the consent dialog.
 */
export const useRuleDetailByline = (
  rule: AgenticLogicRuleDto
): Pick<RuleListRow, "eligibleSurfaces" | "usedIn"> => {
  const templatesQuery = useAgenticLogicTemplates();
  const placementsQuery = useAgenticLogicPlacements();
  const assignmentsQuery = useAgenticLogicAssignments();

  // `data` is undefined until the first success, which covers every
  // not-yet-known state; the explicit undefined on error also discards a
  // stale cached answer.
  const row = buildRuleListRows({
    rules: [rule],
    templates: templatesQuery.isError
      ? undefined
      : templatesQuery.data?.templates,
    placements: placementsQuery.isError
      ? undefined
      : placementsQuery.data?.placements,
    assignments: assignmentsQuery.isError
      ? undefined
      : assignmentsQuery.data?.assignments,
  })[0];

  return {
    eligibleSurfaces: row?.eligibleSurfaces ?? null,
    usedIn: row?.usedIn ?? null,
  };
};
